'use client';

import { useEffect, useState, useRef } from 'react';
import { apiClient } from '@/lib/api';
import GlassCard from '@/components/GlassCard';

interface AdvancedIndicatorsChartProps {
  symbol: string;
  indicators?: string[];
  height?: number;
  width?: number | string;
}

interface IndicatorData {
  indicator: string;
  period?: number;
  values?: number[];
  dates?: string[];
  current_value?: number;
  signal?: string;
  error?: string;
}

interface ChartData {
  symbol: string;
  indicators: Record<string, IndicatorData>;
  timestamp: string;
}

export default function AdvancedIndicatorsChart({ 
  symbol, 
  indicators = ['RSI', 'MACD', 'BB', 'SMA', 'EMA'],
  height = 600,
  width = '100%'
}: AdvancedIndicatorsChartProps) {
  const [data, setData] = useState<ChartData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedIndicator, setSelectedIndicator] = useState<string>('RSI');
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const fetchIndicators = async () => {
      try {
        setLoading(true);
        const response = await apiClient.getAdvancedIndicators(symbol);
        
        if (response.data) {
          setData(response.data);
        } else {
          setError(response.error || 'Failed to fetch indicators');
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to fetch indicators');
      } finally {
        setLoading(false);
      }
    };

    if (symbol) {
      fetchIndicators();
    }
  }, [symbol, indicators.join(',')]);

  useEffect(() => {
    if (canvasRef.current && data && selectedIndicator) {
      drawChart();
    }
  }, [data, selectedIndicator]);

  const drawChart = () => {
    const canvas = canvasRef.current;
    if (!canvas || !data || !data.indicators[selectedIndicator]) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const indicator = data.indicators[selectedIndicator];
    if (indicator.error || !indicator.values) return;

    // Detect theme for colors
    const isDarkMode = document.documentElement.classList.contains('dark');
    const colors = {
      grid: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)',
      text: isDarkMode ? 'rgba(255, 255, 255, 0.6)' : 'rgba(0, 0, 0, 0.6)',
      main: '#2DD4BF', // brand-teal
      danger: '#E879F9', // brand-plum
    };

    // Clear canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Set canvas dimensions
    canvas.width = canvas.offsetWidth;
    canvas.height = canvas.offsetHeight;

    const padding = 60;
    const chartWidth = canvas.width - 2 * padding;
    const chartHeight = canvas.height - 2 * padding;

    // Draw grid
    ctx.strokeStyle = colors.grid;
    ctx.lineWidth = 1;
    
    // Vertical grid lines
    for (let i = 0; i <= 10; i++) {
      const x = padding + (i * chartWidth) / 10;
      ctx.beginPath();
      ctx.moveTo(x, padding);
      ctx.lineTo(x, canvas.height - padding);
      ctx.stroke();
    }

    // Horizontal grid lines
    for (let i = 0; i <= 10; i++) {
      const y = padding + (i * chartHeight) / 10;
      ctx.beginPath();
      ctx.moveTo(padding, y);
      ctx.lineTo(canvas.width - padding, y);
      ctx.stroke();
    }

    // Draw indicator line
    if (indicator.values && indicator.values.length > 0) {
      const values = indicator.values;
      const minValue = Math.min(...values);
      const maxValue = Math.max(...values);
      const range = maxValue - minValue || 1;

      // Gradient for line
      const gradient = ctx.createLinearGradient(0, padding, 0, canvas.height - padding);
      gradient.addColorStop(0, colors.main);
      gradient.addColorStop(1, colors.main + '44');

      ctx.strokeStyle = gradient;
      ctx.lineWidth = 3;
      ctx.lineJoin = 'round';
      ctx.lineCap = 'round';
      ctx.beginPath();

      values.forEach((value, index) => {
        const x = padding + (index * chartWidth) / (values.length - 1);
        const y = canvas.height - padding - ((value - minValue) * chartHeight / range);
        
        if (index === 0) {
          ctx.moveTo(x, y);
        } else {
          ctx.lineTo(x, y);
        }
      });

      ctx.stroke();

      // Shadow for line
      ctx.shadowBlur = 15;
      ctx.shadowColor = colors.main + '33';
      ctx.stroke();
      ctx.shadowBlur = 0;

      // Draw labels
      ctx.fillStyle = colors.text;
      ctx.font = 'bold 10px Inter, sans-serif';
      ctx.textAlign = 'right';
      
      // Y-axis labels
      for (let i = 0; i <= 5; i++) {
        const value = minValue + (i * range / 5);
        const y = canvas.height - padding - (i * chartHeight / 5);
        ctx.fillText(value.toFixed(2), padding - 15, y + 4);
      }

      // X-axis labels (dates)
      if (indicator.dates && indicator.dates.length > 0) {
        ctx.textAlign = 'center';
        const step = Math.max(1, Math.floor(indicator.dates.length / 5));
        for (let i = 0; i < indicator.dates.length; i += step) {
          const x = padding + (i * chartWidth) / (indicator.dates.length - 1);
          const date = new Date(indicator.dates[i]).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
          ctx.fillText(date, x, canvas.height - padding + 25);
        }
      }

      // Special lines for RSI
      if (selectedIndicator === 'RSI') {
        ctx.strokeStyle = colors.danger;
        ctx.lineWidth = 1;
        ctx.setLineDash([4, 4]);
        
        // Overbought line (70)
        const overboughtY = canvas.height - padding - ((70 - minValue) * chartHeight / range);
        ctx.beginPath();
        ctx.moveTo(padding, overboughtY);
        ctx.lineTo(canvas.width - padding, overboughtY);
        ctx.stroke();

        // Oversold line (30)
        const oversoldY = canvas.height - padding - ((30 - minValue) * chartHeight / range);
        ctx.beginPath();
        ctx.moveTo(padding, oversoldY);
        ctx.lineTo(canvas.width - padding, oversoldY);
        ctx.stroke();

        ctx.setLineDash([]);
      }
    }
  };

  const getIndicatorColor = (signal: string | undefined) => {
    switch (signal) {
      case 'Bullish':
      case 'Oversold':
        return 'text-brand-teal-light';
      case 'Bearish':
      case 'Overbought':
        return 'text-brand-plum-light';
      default:
        return 'text-text-primary';
    }
  };

  if (loading) {
    return (
      <GlassCard variant="subtle" className="p-8">
        <div className="flex flex-col items-center justify-center h-64 gap-4">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-brand-teal shadow-glow"></div>
          <span className="text-text-tertiary text-[10px] font-bold uppercase tracking-widest">Neural Feed Syncing...</span>
        </div>
      </GlassCard>
    );
  }

  if (error) {
    return (
      <GlassCard variant="subtle" className="p-8 border-brand-plum/30">
        <div className="flex flex-col items-center justify-center h-64 text-center space-y-3">
          <p className="font-bold text-brand-plum-light uppercase tracking-tight">Sync Exception</p>
          <p className="text-xs text-text-secondary">{error}</p>
        </div>
      </GlassCard>
    );
  }

  return (
    <GlassCard variant="elevated" className="p-6">
      <div className="flex justify-between items-center mb-8">
        <h3 className="text-sm font-bold uppercase tracking-widest text-text-tertiary flex items-center gap-2">
           <div className="w-1.5 h-1.5 rounded-full bg-brand-teal" />
           {symbol} Quantitative Vectors
        </h3>
        <select 
          value={selectedIndicator}
          onChange={(e) => setSelectedIndicator(e.target.value)}
          className="bg-bg-secondary border border-border-primary text-text-primary rounded-xl px-4 py-1.5 text-[10px] font-black uppercase tracking-widest focus:outline-none focus:ring-2 focus:ring-brand-teal/50 transition-all border-white/5"
          aria-label="Select indicator"
        >
          {data?.indicators && Object.keys(data.indicators).map(indicator => (
            <option key={indicator} value={indicator}>
              {indicator}
            </option>
          ))}
        </select>
      </div>

      {data && (
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-8">
          {/* Chart Container */}
          <div className="xl:col-span-2">
            <div className="bg-bg-primary/30 rounded-2xl border border-white/5 p-2 overflow-hidden relative group">
              <canvas 
                ref={canvasRef}
                className="w-full"
                style={{ height: `${height}px` }}
              />
              <div className="absolute top-4 left-4 pointer-events-none">
                 <span className="text-[9px] font-black uppercase tracking-widest text-text-tertiary opacity-30">Real-time Stream Engine v4.0</span>
              </div>
            </div>
          </div>

          {/* Indicators Matrix */}
          <div className="xl:col-span-1 space-y-6">
            <div className="space-y-3">
              <h4 className="text-[10px] font-bold uppercase tracking-widest text-text-tertiary px-1">Heuristic Matrix</h4>
              
              <div className="space-y-2 max-h-[350px] overflow-y-auto pr-2 custom-scrollbar">
                {data.indicators && Object.entries(data.indicators).map(([name, indicator]) => (
                  <div 
                    key={name}
                    className={`p-4 rounded-2xl border transition-all duration-300 cursor-pointer group ${
                      selectedIndicator === name 
                        ? 'bg-brand-teal/10 border-brand-teal/30 shadow-glow-sm' 
                        : 'bg-bg-secondary/30 border-white/5 hover:border-brand-teal/20'
                    }`}
                    onClick={() => setSelectedIndicator(name)}
                  >
                    <div className="flex justify-between items-center">
                      <div>
                        <h5 className="text-xs font-bold text-text-primary uppercase tracking-tight group-hover:text-brand-teal-light transition-colors">{name}</h5>
                        {indicator.error ? (
                          <p className="text-brand-plum-light text-[9px] font-bold uppercase mt-1">Fault</p>
                        ) : (
                          <div className="flex items-center gap-2 mt-1">
                             <div className={`w-1 h-1 rounded-full ${indicator.signal ? 'bg-brand-teal' : 'bg-text-tertiary'} shadow-glow`} />
                             <span className={`text-[10px] font-black tracking-widest uppercase ${getIndicatorColor(indicator.signal)}`}>
                               {indicator.signal || 'Neutral'}
                             </span>
                          </div>
                        )}
                      </div>
                      <div className="text-right">
                         <div className="text-sm font-mono font-bold text-text-primary">
                            {indicator.current_value?.toFixed(2) || 'N/A'}
                         </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Signal Details */}
            {data.indicators[selectedIndicator] && !data.indicators[selectedIndicator].error && (
              <div className="p-5 bg-bg-secondary/30 rounded-2xl border border-white/5 space-y-3">
                <h5 className="text-[10px] font-bold uppercase tracking-widest text-text-tertiary">Intercept Metadata</h5>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-[10px] font-bold uppercase tracking-widest text-text-tertiary">Spot Value</span>
                    <span className="text-sm font-mono font-bold text-text-primary">
                      {data.indicators[selectedIndicator].current_value?.toFixed(4)}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-[10px] font-bold uppercase tracking-widest text-text-tertiary">Schema Phase</span>
                    <span className={`text-[10px] font-black uppercase tracking-widest ${getIndicatorColor(data.indicators[selectedIndicator].signal)}`}>
                      {data.indicators[selectedIndicator].signal || 'Idle'}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-[10px] font-bold uppercase tracking-widest text-text-tertiary">Lookback Period</span>
                    <span className="text-xs font-bold text-text-primary">{data.indicators[selectedIndicator].period || 'Standard'}</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </GlassCard>
  );
}
 